import { Auth, type ProcessEventResponse } from "@saasbase-io/loginflow-websdk";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { PASSKEY_BUTTON_DEFAULT_PROPS as DEFAULT_PROPS } from "./button.defs";
import type {
  PasskeyButtonContent,
  PasskeyButtonIconAlign,
  PasskeyButtonPart,
  PasskeyButtonSize,
  PasskeyButtonVariant,
  SbPasskeyButtonProps as Props,
} from "./button.types";

/**
 * Button that triggers a passkey authentication flow.
 *
 * @part passkey-button - rtg-button: root
 * @part passkey-button-icon - svg/rtg-spinner: icon or loading indicator
 *
 * @event sb-passkey-button:click - Fired when the button is clicked, before the auth call.
 *  Detail: `{ id: string; event?: string }`
 * @event sb-passkey-button:success - Fired after the auth call resolves successfully.
 *  Detail: `{ id: string; event: string; result: ProcessEventResponse }`
 * @event sb-passkey-button:error - Fired when the auth call fails.
 *  Detail: `{ id: string; event: string; message: string }`
 */
@customElement("sb-passkey-button")
export class SbPasskeyButton extends LitElement implements Props {
  static readonly ROOT = "passkey-button";
  static readonly ICON = "icon";

  static readonly TAG = `${NAMESPACE}-${SbPasskeyButton.ROOT}`;

  static readonly CLICK_EVENT = `${SbPasskeyButton.TAG}:click`;
  static readonly SUCCESS_EVENT = `${SbPasskeyButton.TAG}:success`;
  static readonly ERROR_EVENT = `${SbPasskeyButton.TAG}:error`;

  static readonly PARTS: Record<PasskeyButtonPart, string> = {
    root: SbPasskeyButton.ROOT,
    icon: `${SbPasskeyButton.ROOT}-${SbPasskeyButton.ICON}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Visual variant of the button component.
   */
  @property({ type: String })
  variant: PasskeyButtonVariant = DEFAULT_PROPS.variant;

  /**
   * Size of the button component.
   */
  @property({ type: String })
  size: PasskeyButtonSize = DEFAULT_PROPS.size;

  /**
   * Controls what the button renders: icon only, label only, or both.
   */
  @property({ type: String })
  content: PasskeyButtonContent = DEFAULT_PROPS.content;

  /**
   * Side the icon appears on relative to the label.
   */
  @property({ type: String, attribute: "icon-align" })
  iconAlign: PasskeyButtonIconAlign = DEFAULT_PROPS.iconAlign;

  /**
   * Text used as the label of the button component.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /**
   * Event identifier forwarded to the authentication service on click.
   * When omitted, only the custom click event is dispatched.
   */
  @property({ type: String })
  event?: string;

  /**
   * Disables the button, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Manual alternative to the private `_loading` state.
   */
  @property({ type: Boolean })
  loading?: boolean;

  /* ---------------------------------------- */

  /**
   * Indicates whether the component is dispatching or emitting an event.
   */
  @state()
  private _loading = false;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbPasskeyButton.TAG}-${this.seed}`;
  }

  get iconId() {
    return `${this.rootId}-${SbPasskeyButton.ICON}`;
  }

  get isLoading() {
    return this.loading || this._loading;
  }

  get isDisabled() {
    return this.disabled || this.isLoading;
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this._handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
  }

  /* ------------------------------------------------------------ */

  private _handleClick = async () => {
    if (this.isDisabled) return;
    this._loading = true;

    this.dispatchEvent(
      new CustomEvent(SbPasskeyButton.CLICK_EVENT, {
        detail: { id: this.rootId, event: this.event },
        bubbles: true,
        composed: true,
      }),
    );

    try {
      if (this.event) {
        const result: ProcessEventResponse = await Auth.processLoginflowEvent({
          event: this.event,
        });
        this.dispatchEvent(
          new CustomEvent(SbPasskeyButton.SUCCESS_EVENT, {
            detail: { id: this.rootId, event: this.event, result },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.dispatchEvent(
        new CustomEvent(SbPasskeyButton.ERROR_EVENT, {
          detail: { id: this.rootId, event: this.event, message },
          bubbles: true,
          composed: true,
        }),
      );
    } finally {
      this._loading = false;
    }
  };

  /* ------------------------------------------------------------ */

  private _renderIcon() {
    const cn =
      this.content === "icon" ? "" : `rtg-icon-align-${this.iconAlign}`;

    if (this.isLoading)
      return html`
        <rtg-spinner
          id="${this.iconId}"
          part="${SbPasskeyButton.PARTS.icon}"
          class="${cn ?? nothing}"
        ></rtg-spinner>
      `;

    if (!this.content.includes("icon")) return nothing;

    return html`
      <svg
        id="${this.iconId}"
        part="${SbPasskeyButton.PARTS.icon}"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-user-round-key-icon lucide-user-round-key ${cn}"
      >
        <path d="M19 11v6" />
        <path d="M19 13h2" />
        <path d="M2 21a8 8 0 0 1 12.868-6.349" />
        <circle cx="10" cy="8" r="5" />
        <circle cx="19" cy="19" r="2" />
      </svg>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-button
        id="${this.rootId}"
        part="${SbPasskeyButton.PARTS.root}"
        data-event="${this.event ?? nothing}"
        data-loading="${this.isLoading ? "true" : nothing}"
        variant="${this.variant}"
        size="${this.size}"
        ?disabled="${this.isDisabled}"
      >
        ${this.iconAlign === "start" ? this._renderIcon() : nothing}
        ${this.content.includes("label") ? this.label : nothing}
        ${this.iconAlign === "end" ? this._renderIcon() : nothing}
      </rtg-button>
    `;
  }
}
